import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utlils/axios";
import { dispatch } from "../index"
import { handleSnackbar } from "../../utlils/snackbar";

export interface AdminUser {
    userId?: string | number;
    companyId?: string | number;
    loginName: string;
    loginPassword: string;
    firstName: string;
    lastName: string;
    emailId: string;
    mobileNo: string;
    employeeCode: string;
    modelCodeAccess: string;
    isActive?: boolean;
    isAdmin: boolean;
}

export interface CompanyRecord {
    companyId?: string;
    companyName: string;
    companyCode: string;
    adminUser?: AdminUser;
}

export interface CompanyLicenseRecord {
    companyId?: string | number;
    licenseData?: string;
    startDate?: string;
    endDate?: string;
}

export interface resetUserPasswordState {
    userId: string,
    newPassword: string
}

interface StateValue {
    addCompany: CompanyRecord
    companyList: CompanyRecord[]
    companyUsers: AdminUser[]
    updateCompanyUsers: AdminUser,
    updateCompanyDetails: CompanyRecord,
    companyLicenses: CompanyLicenseRecord[],
    resetAdminUserPassword: resetUserPasswordState,
    getMachineFingerPrint: ""
}

const initialState: StateValue = {
    addCompany: {
        companyName: "",
        companyCode: "",
    },
    resetAdminUserPassword: {
        userId: "",
        newPassword: ""
    },
    getMachineFingerPrint: "",
    companyList: [],
    companyUsers: [],
    companyLicenses: [],
    updateCompanyUsers: {
        userId: "",
        companyId: "",
        loginName: "",
        loginPassword: "",
        firstName: "",
        lastName: "",
        emailId: "",
        mobileNo: "",
        employeeCode: "",
        modelCodeAccess: "",
        isActive: true,
        isAdmin: true,
    },
    updateCompanyDetails: {
        companyName: "",
        companyCode: "",
    }
}

const addCompanySlice = createSlice({
    name: "companyInfo",
    initialState,
    reducers: {
        addCompanyDetailsData(state, action) {
            state.addCompany = action.payload
        },
        updateCompanyDetailsData(state, action) {
            state.updateCompanyDetails = action.payload
        },
        updateUsersDetailsData(state, action) {
            state.updateCompanyUsers = action.payload
        },
        getCompanyDetailsData(state, action) {
            state.companyList = action.payload
        },
        getCompanyUsersData(state, action) {
            state.companyUsers = action.payload
        },
        getCompanyLicensesData(state, action) {
            state.companyLicenses = action.payload
        },
        clearCompanyUsersData(state) {
            state.companyUsers = []
        },
        clearCompanyLicensesData(state) {
            state.companyLicenses = []
        },
        updateRestAdminUserPasswordData(state, action) {
            state.resetAdminUserPassword = action.payload
        },
        getMachineFingerPrintData(state, action) {
            state.getMachineFingerPrint = action.payload
        }
    }
})

export default addCompanySlice.reducer

export const addCompanyDetails = (payload: CompanyRecord) => {
    return async () => {
        const url = '/admin/company'
        try {
            const response = await axios.post(url, payload)
            dispatch(addCompanySlice.actions.addCompanyDetailsData(response.data))
            return response
        } catch (error) {
            dispatch(handleSnackbar(error));
        }
    }
}

export const getMachineFingerPrintValue = () => {
    return async () => {
        const url = '/auth/fingerprint'
        try {
            const response = await axios.get(url)
            dispatch(addCompanySlice.actions.getMachineFingerPrintData(response.data))
            return response
        } catch (error) {
            dispatch(handleSnackbar(error));
        }
    }
}

export const restAdminUserPasswordDetails = (payload: resetUserPasswordState) => {
    return async () => {
        const url = '/admin/reset-password'
        try {
            const response = await axios.post(url, payload)
            dispatch(addCompanySlice.actions.updateRestAdminUserPasswordData(response.data))
            dispatch(handleSnackbar(response?.data))
            return response
        } catch (error) {
            dispatch(handleSnackbar(error));
        }
    }
}

export const getCompanyDetails = () => {
    return async () => {
        const url = '/admin/companies'
        try {
            const response = await axios.get(url)
            dispatch(addCompanySlice.actions.getCompanyDetailsData(response.data))
        } catch (error) {
            dispatch(handleSnackbar(error));
        }
    }
}

export const getCompanyUsers = (companyId: string) => {
    return async () => {
        const url = `/admin/company-users/${companyId}`
        try {
            const response = await axios.get(url)
            dispatch(addCompanySlice.actions.getCompanyUsersData(response.data))
            return response
        } catch (error) {
            dispatch(handleSnackbar(error));
        }
    }
}

export const getCompanyLicenses = (companyId: string | number) => {
    return async () => {
        const url = `/admin/company-license/${String(companyId)}`
        try {
            const response = await axios.get(url)
            dispatch(addCompanySlice.actions.getCompanyLicensesData(response.data))
            return response
        } catch (error) {
            dispatch(handleSnackbar(error));
        }
    }
}

export const updateCompanyUser = (
    companyId: string,
    userId: string | number,
    payload: Partial<AdminUser>
) => {
    return async () => {
        const url = `/admin/company/${companyId}/${userId}`
        try {
            const response = await axios.put(url, payload)
            dispatch(addCompanySlice.actions.updateUsersDetailsData(response?.data))
            return response
        } catch (error) {
            dispatch(handleSnackbar(error));
        }
    }
}

export const updateCompanyDetails = (
    payload: Partial<AdminUser>
) => {
    return async () => {
        const url = "/admin/company"
        try {
            const response = await axios.put(url, payload)
            dispatch(addCompanySlice.actions.updateCompanyDetailsData(response?.data))
            return response
        } catch (error) {
            dispatch(handleSnackbar(error));
        }
    }
}

export const uploadCompanyLicense = (
    companyId: string | number,
    file: File
) => {
    return async () => {
        const url = `/admin/company-license/${String(companyId)}`
        try {
            const formData = new FormData()
            formData.append("file", file)

            const response = await axios.post(url, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            dispatch(handleSnackbar(response?.data))
            return response
        } catch (error) {
            dispatch(handleSnackbar(error));
        }
    }
}
